import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CameraAlt,
  CheckCircle,
  Error as ErrorIcon,
  FaceRetouchingNatural,
  Verified,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';

interface FaceRecognitionProps {
  onSignatureComplete?: (signatureData: any) => void;
}

type RecognitionStatus = 'idle' | 'capturing' | 'processing' | 'success' | 'failed';
type Mode = 'verify' | 'enroll';

export default function FaceRecognition({ onSignatureComplete }: FaceRecognitionProps) {
  const { doctor, reloadCurrentDoctor } = useUser();
  const [mode, setMode] = useState<Mode>('verify');
  const [status, setStatus] = useState<RecognitionStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [matchScore, setMatchScore] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<any>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup camera stream on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setShowCamera(true);
      setStatus('idle');
      setError(null);
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera. Please grant camera permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setStatus('idle');
    setMode('verify'); // Reset mode when closing
  };

  const enrollFace = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setStatus('capturing');
    setError(null);
    setProgress(0);

    // Capture image from video
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    const imageData = canvas.toDataURL('image/jpeg');

    // Call backend API for face enrollment
    setStatus('processing');
    setProgress(50);

    // Check if doctor is selected
    if (!doctor) {
      setStatus('failed');
      setError('No doctor selected. Please select a doctor from the toolbar first.');
      return;
    }

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/face/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          doctor_id: doctor.doctor_id,
          image_base64: imageData,
          replace_existing: true,
        }),
      });

      setProgress(80);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Face enrollment failed');
      }

      const result = await response.json();
      setProgress(100);

      // Success
      setStatus('success');
      setEnrollmentSuccess(true);

      // Reload doctor data to update has_face_enrolled status
      await reloadCurrentDoctor();

      // Stop camera after successful enrollment
      setTimeout(() => {
        stopCamera();
      }, 2000);
    } catch (err) {
      console.error('Face enrollment error:', err);
      setStatus('failed');
      setError(err instanceof Error ? err.message : 'Face enrollment failed. Please try again.');
      setProgress(0);
    }
  };

  const captureAndRecognize = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setStatus('capturing');
    setError(null);
    setProgress(0);

    // Capture image from video
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    const imageData = canvas.toDataURL('image/jpeg');

    // Call backend API for face recognition
    setStatus('processing');
    setProgress(50);

    // Check if doctor is selected
    if (!doctor) {
      setStatus('failed');
      setError('No doctor selected. Please select a doctor from the toolbar first.');
      return;
    }

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/face/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          doctor_id: doctor.doctor_id,
          image_base64: imageData,
        }),
      });

      setProgress(80);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Face verification failed');
      }

      const result = await response.json();
      setProgress(100);
      setMatchScore(result.match_score);

      if (result.verified) {
        // Success
        const signature = {
          doctorId: result.doctor_id,
          doctorName: result.doctor_name,
          timestamp: new Date().toISOString(),
          matchScore: result.match_score,
          imageData,
          verified: true,
        };

        setSignatureData(signature);
        setStatus('success');
        onSignatureComplete?.(signature);

        // Stop camera after successful capture
        setTimeout(() => {
          stopCamera();
        }, 2000);
      } else {
        // Failed - match score below threshold
        setStatus('failed');
        setError(`Face match score (${(result.match_score * 100).toFixed(1)}%) is below the required threshold (${(result.threshold * 100).toFixed(1)}%). Please try again.`);
      }
    } catch (err) {
      console.error('Face recognition error:', err);
      setStatus('failed');
      setError(err instanceof Error ? err.message : 'Face recognition failed. Please try again.');
      setProgress(0);
    }
  };

  const handleRetry = () => {
    setStatus('idle');
    setError(null);
    setProgress(0);
    setMatchScore(0);
  };

  const handleClose = () => {
    stopCamera();
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'idle':
        return {
          icon: <CameraAlt sx={{ fontSize: 64, color: '#1976d2' }} />,
          message: mode === 'enroll' ? 'Position your face to enroll' : 'Position your face in the frame',
          color: '#1976d2',
        };
      case 'capturing':
        return {
          icon: <FaceRetouchingNatural sx={{ fontSize: 64, color: '#ff9800' }} />,
          message: 'Capturing image...',
          color: '#ff9800',
        };
      case 'processing':
        return {
          icon: <FaceRetouchingNatural sx={{ fontSize: 64, color: '#ff9800' }} />,
          message: mode === 'enroll' ? 'Processing face enrollment...' : 'Processing face recognition...',
          color: '#ff9800',
        };
      case 'success':
        return {
          icon: <CheckCircle sx={{ fontSize: 64, color: '#009688' }} />,
          message: mode === 'enroll' ? 'Face enrolled successfully!' : 'Face verified successfully!',
          color: '#009688',
        };
      case 'failed':
        return {
          icon: <ErrorIcon sx={{ fontSize: 64, color: '#e91e63' }} />,
          message: mode === 'enroll' ? 'Enrollment failed' : 'Verification failed',
          color: '#e91e63',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box>
      {/* Trigger Button */}
      {!showCamera && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Digital Signature Verification
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use face recognition to digitally sign prescriptions and documents
          </Typography>

          {enrollmentSuccess && (
            <Alert
              severity="success"
              icon={<Verified />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                <strong>Face Enrolled Successfully!</strong>
                <br />
                You can now use face recognition for digital signatures.
              </Typography>
            </Alert>
          )}

          {signatureData ? (
            <Alert
              severity="success"
              icon={<Verified />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                <strong>Signature Verified</strong>
                <br />
                Match Score: {(signatureData.matchScore * 100).toFixed(1)}%
                <br />
                Timestamp: {new Date(signatureData.timestamp).toLocaleString()}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {doctor?.has_face_enrolled
                  ? 'Click below to start face recognition for digital signature'
                  : 'First enroll your face, then use it for digital signatures'}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            {!doctor?.has_face_enrolled && (
              <Button
                variant="contained"
                startIcon={<FaceRetouchingNatural />}
                onClick={() => {
                  setMode('enroll');
                  startCamera();
                }}
                sx={{ bgcolor: '#ff9800' }}
              >
                Enroll Face
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<CameraAlt />}
              onClick={() => {
                setMode('verify');
                startCamera();
              }}
              sx={{ bgcolor: '#1976d2' }}
              disabled={!doctor?.has_face_enrolled}
            >
              Start Face Recognition
            </Button>
          </Box>
        </Paper>
      )}

      {/* Camera Dialog */}
      <Dialog
        open={showCamera}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FaceRetouchingNatural sx={{ color: mode === 'enroll' ? '#ff9800' : '#1976d2' }} />
            <Typography variant="h6">
              {mode === 'enroll' ? 'Face Enrollment' : 'Face Recognition'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Video Preview */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              bgcolor: '#000',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2,
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />

            {/* Face Detection Overlay */}
            {status === 'idle' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '60%',
                  height: '70%',
                  border: '3px dashed rgba(255, 255, 255, 0.5)',
                  borderRadius: '50% 50% 45% 45%',
                }}
              />
            )}

            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>

          {/* Status Display */}
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{ mb: 2 }}>{config.icon}</Box>
            <Typography variant="h6" sx={{ color: config.color, mb: 1 }}>
              {config.message}
            </Typography>

            {(status === 'processing' || status === 'capturing') && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: config.color,
                    },
                  }}
                />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {progress}%
                </Typography>
              </Box>
            )}

            {status === 'success' && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Match: ${(matchScore * 100).toFixed(1)}%`}
                  sx={{ bgcolor: '#009688', color: 'white', fontWeight: 600 }}
                />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          {status === 'failed' && (
            <Button onClick={handleRetry} variant="contained" color="warning">
              Retry
            </Button>
          )}
          {status === 'idle' && (
            <Button
              onClick={mode === 'enroll' ? enrollFace : captureAndRecognize}
              variant="contained"
              sx={{ bgcolor: mode === 'enroll' ? '#ff9800' : '#1976d2' }}
            >
              {mode === 'enroll' ? 'Capture & Enroll' : 'Capture & Verify'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
